import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useCallback, useRef } from "react";
import { useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type addUserToAccount_AddUserToAccountMutation } from "@relay/addUserToAccount_AddUserToAccountMutation.graphql";
import { ADD_USER_TO_ACCOUNT_MUTATION } from "./add-user-to-account.graphql";
import { type AddUserToAccountProps } from "./add-user-to-account.interface";
import { SelectSingleUserForm } from "../../../../features/users/select-single-user-form";
import { RegisterUser } from "../register-user";

export const AddUserToAccount = ({
	onUserAdded,
	accountId,
	connections,
}: AddUserToAccountProps) => {
	const { t } = useTranslation(["common", "errors"]);

	const [addUserToAccount, isAddingUserToAccount] =
		useMutation<addUserToAccount_AddUserToAccountMutation>(ADD_USER_TO_ACCOUNT_MUTATION);

	const toastRef = useRef<Toast>(null);

	const handleOnUsersSelected = useCallback(
		(userIds: string | string[]) => {
			const userId = Array.isArray(userIds) ? userIds[0] : userIds;

			addUserToAccount({
				variables: {
					input: {
						userId,
						accountId,
					},
					connections: connections || [],
				},
				onCompleted: () => {
					onUserAdded(userId);
				},
				onError: () => {
					toastRef.current?.show({
						severity: "error",
						summary: t("errors:generic"),
						detail: t("errors:userAddFailed"),
					});
				},
			});
		},
		[addUserToAccount, accountId, toastRef.current],
	);

	return (
		<TabView>
			<TabPanel header={t("common:users.registerTab")}>
				<RegisterUser
					accountId={accountId}
					connections={connections}
					onUserRegistered={onUserAdded}
				/>
			</TabPanel>
			<TabPanel header={t("common:users.selectTab")}>
				<SelectSingleUserForm
					disabled={isAddingUserToAccount}
					onUserSelected={handleOnUsersSelected}
				/>
			</TabPanel>
			<Toast ref={toastRef} />
		</TabView>
	);
};
